import type { AudienceCreateFormValue } from './audience-create.component';

// Regole pure usate da AudienceCreateComponent.buildSubmissionPayload / validateCompositionRules.
// Estratte per poterle testare senza montare il form: il componente si occupa solo degli effetti
// (errori sui controlli, scroll, notifiche).

export type CompositionError = 'tagsRequired' | 'interestFilterRequired' | 'ruleFilterRequired';

/**
 * Verifica che la composizione abbia il minimo indispensabile per il tipo di audience:
 * custom query → sempre valida; rule-based → almeno un gruppo con payload; interests → almeno
 * un tag, oppure (senza tag, previa conferma) almeno un paese o un genere: senza tag né filtri
 * l'audience non avrebbe alcun criterio e il backend la rifiuta.
 * 'tagsRequired' non è bloccante: il componente chiede conferma e consente il salvataggio.
 */
export function findCompositionError(
  formValue: Pick<AudienceCreateFormValue, 'aiTags' | 'ruleBlocks'> & Partial<Pick<AudienceCreateFormValue, 'location' | 'gender'>>,
  kind: { isCustomQuery: boolean; isRuleBased: boolean; allLocationsLabel?: string }
): CompositionError | null {
  if (kind.isCustomQuery) {
    return null;
  }

  if (!kind.isRuleBased) {
    const hasTags = Array.isArray(formValue.aiTags) && formValue.aiTags.length > 0;
    if (hasTags) {
      return null;
    }
    return hasCountryOrGender(formValue, kind.allLocationsLabel) ? 'tagsRequired' : 'interestFilterRequired';
  }

  const hasRuleFilter = (formValue.ruleBlocks || []).some(block =>
    (block.groups || []).some(group => Array.isArray(group.payload) && group.payload.length > 0)
  );
  return hasRuleFilter ? null : 'ruleFilterRequired';
}

/** true se è selezionato un paese specifico o un genere (stessi valori "tutti" di buildInterestFilters). */
export function hasCountryOrGender(
  formValue: Partial<Pick<AudienceCreateFormValue, 'location' | 'gender'>>,
  allLocationsLabel = 'All Locations'
): boolean {
  const location = (formValue.location ?? '').trim();
  const hasCountry = !!location && location !== allLocationsLabel && location !== 'All Locations';
  const hasGender = (formValue.gender ?? []).some(gender => gender !== 'All Gender' && gender !== 'Unknown');
  return hasCountry || hasGender;
}

/** true se al salvataggio è stata espressa una scelta di accuratezza (livello, volume o punto sul grafico). */
export function hasAccuracySelection(
  formValue: Pick<AudienceCreateFormValue, 'audienceQuality' | 'extendVolumes'>,
  hasGraphSelection: boolean
): boolean {
  const hasAccuracyLevel = !!formValue.audienceQuality && ['high', 'medium', 'low'].includes(formValue.audienceQuality);
  const hasVolumeNumber = !!formValue.extendVolumes;
  return hasAccuracyLevel || hasVolumeNumber || hasGraphSelection;
}

/**
 * Per l'Italia la voce UI "All Regions" equivale a nessuna regione: il payload la omette e il
 * backend la interpreta come tutto il paese. Per gli altri paesi le regioni passano invariate.
 */
export function normalizeSubmissionRegions(selectedCountryCode: string | null, regions: string[] | null | undefined): string[] {
  const list = regions ?? [];
  if (selectedCountryCode === 'IT' && list.length > 0 && list.includes('All Regions')) {
    return [];
  }
  return list;
}

/**
 * Tag da inviare al backend. Ordine: tag del form, poi tag suggeriti, poi la descrizione come
 * unico tag, poi i tag già salvati (in edit). Rule-based → nessun tag. Un'audience interests
 * confermata senza tag (confirmedWithoutTags) va inviata davvero senza: nessun fallback.
 */
export function resolvePayloadTags(
  formValue: Pick<AudienceCreateFormValue, 'aiTags' | 'aiDescription' | 'confirmedWithoutTags'>,
  context: { isRuleBased: boolean; providedTags?: string[] | null; existingTags?: string[] | null }
): string[] | null {
  if (context.isRuleBased || formValue.confirmedWithoutTags) {
    return null;
  }
  const manualTags = (formValue.aiTags ?? [])
    .map(tag => tag?.toString().trim())
    .filter((tag): tag is string => !!tag);
  if (manualTags.length) {
    return manualTags;
  }
  if (context.providedTags?.length) {
    return context.providedTags;
  }
  const description = formValue.aiDescription?.toString().trim();
  if (description) {
    return [description];
  }
  return context.existingTags ?? null;
}

/**
 * ExpandSimilarUsers da inviare al backend. Rule-based e custom query: scelta dell'utente.
 * Interests: si espande solo se ci sono tag — senza tag non c'è nulla su cui calcolare la similarità.
 */
export function resolveExpandSimilarUsers(
  context: { isRuleBased: boolean; isCustomQuery: boolean; formValue: boolean | null | undefined; tags: string[] | null }
): boolean {
  if (context.isRuleBased || context.isCustomQuery) {
    return !!context.formValue;
  }
  return !!context.tags?.length;
}
